// src/context/AuthContext.js
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabase";
import { authAPI, adminAPI } from "../lib/api";
import {
  AUTH_CHANGED_EVENT,
  AUTH_FORCE_LOGOUT_EVENT,
  clearApiCache,
  clearVisitAuthSession,
  getAuthToken,
  getVisitAuthToken,
  getVisitAuthUser,
  hasVisitAuthSession,
  sessionIsDead,
  setVisitAuthUser,
} from "../lib/api";
import { safeGetItem, safeSetItem, safeRemoveItem } from "../lib/safeStorage";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

const buildDefaultPermissions = (role) => {
  const normalized = String(role || "").toLowerCase();
  if (normalized === "owner" || normalized === "admin") {
    return [
      "calendar:read",
      "calendar:write",
      "clients:read",
      "clients:write",
      "forms:read",
      "forms:write",
      "forms:send",
      "forms:delete",
      "integrations:google:manage",
      "staff:manage",
      "locations:manage",
      "services:manage",
      "settings:manage",
      "billing:manage",
    ];
  }
  if (normalized === "staff" || normalized === "reception") {
    return [
      "calendar:read",
      "calendar:write",
      "clients:read",
      "clients:write",
      "forms:read",
      "forms:write",
      "forms:send",
    ];
  }
  return [];
};

const clearLocalAuth = () => {
  clearApiCache();
  if (hasVisitAuthSession()) {
    clearVisitAuthSession();
  } else {
    safeRemoveItem("token");
    safeRemoveItem("user");
  }
  try {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  } catch {
    /* sandbox kann dispatchEvent blockieren */
  }
};

const isAbortLike = (err) => {
  const name = err?.name || err?.cause?.name;
  const msg = String(err?.message || err?.cause?.message || "").toLowerCase();
  const code = err?.code;
  return name === "AbortError" || msg.includes("aborted") || code === "ERR_CANCELED";
};

const buildFallbackUser = (session) => {
  const meta = session.user.user_metadata || {};
  const rawRole = String(meta.role || "staff").toLowerCase();
  const role = rawRole === "admin" ? "owner" : rawRole;
  return {
    id: session.user.id || null,
    email: session.user.email || null,
    org_id: meta.org_id || meta.orgId || null,
    role,
    first_name: meta.first_name || meta.firstname || null,
    last_name: meta.last_name || meta.lastname || null,
    phone: meta.phone || null,
    avatar_url: meta.avatar_url || null,
    active: true,
    email_verified_at: null,
    staff_id: meta.staff_id || null,
    all_locations_access: role === "owner" || role === "admin",
    accessible_location_ids: Array.isArray(meta.accessible_location_ids) ? meta.accessible_location_ids : [],
    is_staff_user: role === "staff",
    permissions: buildDefaultPermissions(role),
  };
};

const buildAdminFallbackUser = (session, adminProfile = {}) => ({
  id: session.user.id || adminProfile.user_id || null,
  email: session.user.email || adminProfile.email || null,
  org_id: null,
  role: adminProfile.role || "super_admin",
  first_name: null,
  last_name: null,
  phone: null,
  avatar_url: null,
  active: adminProfile.active !== false,
  email_verified_at: null,
  staff_id: null,
  all_locations_access: true,
  accessible_location_ids: [],
  is_staff_user: false,
  permissions: [],
  is_admin_user: true,
});

const getCurrentSupabaseAccessToken = async () => {
  const visitToken = getAuthToken();
  if (visitToken && hasVisitAuthSession()) return visitToken;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || getAuthToken() || null;
  } catch {
    return getAuthToken();
  }
};

const localizeAuthErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  const raw = String(err?.message || "").trim().toLowerCase();
  if (!raw) return fallback;
  if (raw.includes("invalid login credentials") || raw.includes("invalid credentials")) {
    return "E-Mail oder Passwort ist falsch.";
  }
  if (raw.includes("email not confirmed") || raw.includes("email not verified")) {
    return "Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.";
  }
  if (raw.includes("already registered")) {
    return "Diese E-Mail-Adresse ist bereits registriert.";
  }
  if (raw.includes("rate limit") || raw.includes("too many requests")) {
    return "Zu viele Versuche. Bitte warten Sie kurz und versuchen Sie es erneut.";
  }
  return fallback;
};

const readStoredUser = () => {
  try {
    // Never combine a visit token with the regular account profile.
    if (hasVisitAuthSession()) return getVisitAuthUser();
    const raw = safeGetItem("user");
      return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isPasswordSetupFlow = () => {
  if (typeof window === "undefined") return false;
  const pathname = String(window.location.pathname || "");
  if (pathname !== "/set-password") return false;

  const search = new URLSearchParams(window.location.search || "");
  const hash = String(window.location.hash || "").replace(/^#/, "");
  const hashParams = new URLSearchParams(hash);

  const flow = search.get("flow");
  const type = search.get("type") || hashParams.get("type");
  const hasRecoveryTokens =
    Boolean(search.get("code")) ||
    Boolean(search.get("token_hash")) ||
    Boolean(hashParams.get("code")) ||
    Boolean(hashParams.get("token_hash")) ||
    Boolean(hashParams.get("access_token"));

  return flow === "invite" || flow === "recovery" || type === "recovery" || hasRecoveryTokens;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readStoredUser());

  const [token, setToken] = useState(null);

  // WICHTIG: NICHT initial anhand token "eingeloggt" sein – erst Session prüfen
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // verhindert parallele hydrates / StrictMode Doppel-Init Chaos
  const hydratingRef = useRef(false);
  const mountedRef = useRef(false);
  const signingOutRef = useRef(false);
  const sessionRevisionRef = useRef(0);
  const observedVisitTokenRef = useRef(getVisitAuthToken());

  const safeSet = useCallback((fn) => {
    if (!mountedRef.current) return;
    fn();
  }, []);

  const setSessionState = useCallback(async (session, opts = {}) => {
    const { silent = false } = opts;

    if (signingOutRef.current) {
      return;
    }

    const revision = ++sessionRevisionRef.current;
    const isCurrent = () => mountedRef.current && !signingOutRef.current && sessionRevisionRef.current === revision;

    if (!session?.user) {
      clearLocalAuth();
      safeSet(() => {
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      });
      return;
    }

    if (readStoredUser()?.id !== session.user.id) {
      safeRemoveItem("user");
      safeSet(() => setUser(null));
    }

    // Token fürs Backend speichern
    if (session.access_token) {
      clearApiCache();
      safeSetItem("token", session.access_token);
      safeSet(() => {
        setToken(session.access_token);
        setIsAuthenticated(true);
      });
      window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
    }

    try {
      let profile = null;
      let adminProfile = null;

      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) profile = await res.json();
      } catch (err) {
        if (!isAbortLike(err)) {
          if (!silent) console.warn("auth/me failed:", err);
        }
      }

      if (!isCurrent()) return;

      if (!profile) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/admin/me`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (res.ok) {
            adminProfile = await res.json();
            profile = buildAdminFallbackUser(session, adminProfile);
          }
        } catch (err) {
          if (!isAbortLike(err) && !silent) {
            console.warn("admin/me failed:", err);
          }
        }
      }

      if (!isCurrent()) return;

      if (!profile) {
        const { data: localProfile, error } = await supabase
          .from("users")
          .select("id, org_id, email, role, first_name, last_name, phone, avatar_url, active")
          .eq("id", session.user.id)
          .single();
        if (!error && localProfile) profile = localProfile;
      }

      if (!isCurrent()) return;

      // Kein Profil kann zweierlei heißen: vorübergehende Störung (dann sind die
      // Fallbacks unten richtig und halten die App am Leben) oder eine serverseitig
      // tote Session. Im zweiten Fall darf der Login NICHT aus dem localStorage
      // rekonstruiert werden - sonst rendert die App einen Schein-Login, in dem jeder
      // Request 401 liefert und aus dem man ohne manuelles Löschen des Storage nicht
      // mehr herauskommt.
      const dead = !profile && !hasVisitAuthSession() && (await sessionIsDead());
      if (!isCurrent()) return;
      if (dead) {
        signingOutRef.current = true;
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch {
          // ignore
        } finally {
          clearLocalAuth();
          safeSet(() => {
            setUser(null);
            setToken(null);
            setIsAuthenticated(false);
          });
          signingOutRef.current = false;
        }
        return;
      }

      if (!profile) {
        const persisted = readStoredUser();
        if (persisted && persisted.id && persisted.id === session.user.id) {
          safeSetItem("user", JSON.stringify(persisted));
          safeSet(() => setUser(persisted));
          return;
        }
      }

      if (!profile) {
        const fallback = buildFallbackUser(session);
        if (hasVisitAuthSession()) {
          setVisitAuthUser(fallback);
        } else {
          safeSetItem("user", JSON.stringify(fallback));
        }
        safeSet(() => setUser(fallback));
        return;
      }

      if (!Array.isArray(profile.permissions)) {
        profile = {
          ...profile,
          permissions: [],
          accessible_location_ids: Array.isArray(profile.accessible_location_ids) ? profile.accessible_location_ids : [],
          all_locations_access: Boolean(profile.all_locations_access),
          is_staff_user: profile.is_staff_user === true || profile.role === "staff",
        };
      }

      if (hasVisitAuthSession()) {
        setVisitAuthUser(profile);
      } else {
        safeSetItem("user", JSON.stringify(profile));
      }
      safeSet(() => setUser(profile));
    } catch (err) {
      if (isAbortLike(err)) return;
      throw err;
    }
  }, [safeSet]);

  const hydrateFromSession = useCallback(async (opts = {}) => {
    const { silent = false } = opts;

    // StrictMode: init läuft 2x → wir lassen nur einen durch
    if (hydratingRef.current) return;
    hydratingRef.current = true;
    const revision = sessionRevisionRef.current;
    const isCurrent = () => mountedRef.current && !signingOutRef.current && revision === sessionRevisionRef.current;

    try {
      if (signingOutRef.current) return;
      const visitToken = getVisitAuthToken();
      if (visitToken) {
        try {
          const res = await authAPI.me();
          if (!isCurrent()) return;
          const profile = res?.data || getVisitAuthUser();
          if (profile) {
            setVisitAuthUser(profile);
            safeSet(() => {
              setToken(visitToken);
              setUser(profile);
              setIsAuthenticated(true);
            });
            return;
          }
        } catch (err) {
          if (!isCurrent()) return;
          if (!silent && !isAbortLike(err)) console.warn("visit auth/me failed:", err);
          const cached = getVisitAuthUser();
          if (![401, 403].includes(err?.response?.status) && cached) {
            safeSet(() => {
              setToken(visitToken);
              setUser(cached);
              setIsAuthenticated(true);
            });
            return;
          }
        }
        clearVisitAuthSession();
      }

      const { data, error } = await supabase.auth.getSession();

      if (!isCurrent()) return;

      if (error) {
        // The SDK emits SIGNED_OUT for invalid refresh credentials. A failed
        // refresh request alone must not discard a recoverable local session.
        if (!silent) console.warn("getSession error:", error);
        return;
      }

      if (data?.session?.user) {
        await setSessionState(data.session, { silent });
      } else {
        const storedToken = getAuthToken();
        if (storedToken) {
          try {
            const res = await authAPI.me();
            if (!isCurrent()) return;
            const profile = res?.data || null;
            if (profile) {
              if (hasVisitAuthSession()) {
                setVisitAuthUser(profile);
              } else {
                safeSetItem("user", JSON.stringify(profile));
              }
              safeSet(() => {
                setToken(storedToken);
                setUser(profile);
                setIsAuthenticated(true);
              });
              return;
            }
          } catch (err) {
            if (!silent && !isAbortLike(err)) console.warn("fallback auth/me failed:", err);
          }
        }

        if (!isCurrent()) return;
        clearLocalAuth();
        safeSet(() => {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        });
      }
    } catch (err) {
      if (isAbortLike(err)) return;
      throw err;
    } finally {
      hydratingRef.current = false;
    }
  }, [setSessionState, safeSet]);

  useEffect(() => {
    mountedRef.current = true;

    let unsub = null;
    let cancelled = false;

    let profileTimer = null;

    // Keep the auth callback synchronous: Supabase invokes it while holding
    // the refresh lock. Profile requests must run after that lock is released.
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      const revision = ++sessionRevisionRef.current;
      window.clearTimeout(profileTimer);

      if (signingOutRef.current) return;
      const visitToken = getVisitAuthToken();
      if (visitToken) {
        safeSet(() => {
          setToken(visitToken);
          setUser(getVisitAuthUser());
          setIsAuthenticated(true);
          setLoading(false);
        });
        return;
      }

      if (!session?.user) {
        // INITIAL_SESSION may be empty following a retryable refresh error.
        // Only an explicit sign-out invalidates a previously loaded session.
        if (event === "SIGNED_OUT") {
          clearLocalAuth();
          safeSet(() => {
            setUser(null);
            setToken(null);
            setIsAuthenticated(false);
            setLoading(false);
          });
        }
        return;
      }

      if (readStoredUser()?.id !== session.user.id) {
        safeRemoveItem("user");
        safeSet(() => setUser(null));
      }
      safeSetItem("token", session.access_token);
      safeSet(() => {
        setToken(session.access_token);
        setIsAuthenticated(true);
      });
      if (isPasswordSetupFlow()) {
        safeSet(() => setLoading(false));
        return;
      }

      profileTimer = window.setTimeout(async () => {
        if (cancelled || revision !== sessionRevisionRef.current) return;
        try {
          await setSessionState(session, { silent: true });
        } catch (error) {
          // A failed profile request does not revoke the authenticated session.
          if (!isAbortLike(error)) console.warn("Session profile could not be refreshed");
        } finally {
          if (!cancelled) safeSet(() => setLoading(false));
        }
      }, 0);
    });
    unsub = data.subscription;

    (async () => {
      try {
        await hydrateFromSession({ silent: true });
      } catch (error) {
        if (!isAbortLike(error)) console.warn("Session could not be loaded; retry on reconnect");
      } finally {
        if (!cancelled) safeSet(() => setLoading(false));
      }
    })();

    // Retry when the network returns, including during initial hydration.
    const onOnline = () => {
      hydrateFromSession({ silent: true }).catch(() => {});
    };
    window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      mountedRef.current = false;
      sessionRevisionRef.current += 1;
      window.clearTimeout(profileTimer);
      window.removeEventListener("online", onOnline);
      if (unsub) unsub.unsubscribe();
    };
  }, [hydrateFromSession, setSessionState, safeSet]);

  useEffect(() => {
    const syncFromStorage = async () => {
      if (signingOutRef.current) {
        safeSet(() => {
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        });
        return;
      }

      // Visit transitions invalidate normal-account profile requests. Normal
      // auth notifications can originate inside hydration and must not cancel it.
      const visitTokenAtStart = getVisitAuthToken();
      const visitChanged = observedVisitTokenRef.current !== visitTokenAtStart;
      observedVisitTokenRef.current = visitTokenAtStart;
      const revision = (visitChanged || visitTokenAtStart) ? ++sessionRevisionRef.current : sessionRevisionRef.current;
      if (hasVisitAuthSession()) safeSet(() => setUser(getVisitAuthUser()));
      const t = await getCurrentSupabaseAccessToken();
      if (signingOutRef.current || revision !== sessionRevisionRef.current || visitTokenAtStart !== getVisitAuthToken()) return;
      let nextUser = readStoredUser();
      if (t && hasVisitAuthSession() && !nextUser) {
        try {
          const res = await authAPI.me();
          if (signingOutRef.current || revision !== sessionRevisionRef.current || visitTokenAtStart !== getVisitAuthToken()) return;
          nextUser = res?.data || null;
          if (nextUser) setVisitAuthUser(nextUser);
        } catch {
          nextUser = null;
        }
      }
      if (signingOutRef.current || revision !== sessionRevisionRef.current || visitTokenAtStart !== getVisitAuthToken()) return;
      safeSet(() => {
        setToken(t);
        setIsAuthenticated(Boolean(t));
        setUser(nextUser);
      });
    };

    const onStorage = (e) => {
      if (e.key === "token" || e.key === "user") syncFromStorage();
    };

    try {
      window.addEventListener("storage", onStorage);
      window.addEventListener(AUTH_CHANGED_EVENT, syncFromStorage);
    } catch { /* sandbox kann addEventListener blockieren */ }

    return () => {
      try {
        window.removeEventListener("storage", onStorage);
        window.removeEventListener(AUTH_CHANGED_EVENT, syncFromStorage);
      } catch { /* ignore */ }
    };
  }, [safeSet]);

  useEffect(() => {
    if (isPasswordSetupFlow()) return undefined;

    const handleForcedLogout = async () => {
      sessionRevisionRef.current += 1;
      signingOutRef.current = true;
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch {
        // ignore
      } finally {
        clearLocalAuth();
        safeSet(() => {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        });
        signingOutRef.current = false;
      }
    };

    try {
      window.addEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForcedLogout);
    } catch {
      // ignore
    }

    return () => {
      try {
        window.removeEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForcedLogout);
      } catch {
        // ignore
      }
    };
  }, [safeSet]);

  useEffect(() => {
    if (isPasswordSetupFlow()) return undefined;
    if (!isAuthenticated) return undefined;

    let active = true;
    const isAdminUser = Boolean(user?.is_admin_user) || String(user?.role || "").toLowerCase() === "super_admin";

    let heartbeatPending = false;
    let heartbeatStartedAt = -Infinity;
    const heartbeat = async () => {
      if (!active || heartbeatPending || document.visibilityState !== 'visible' || Date.now() - heartbeatStartedAt < 15000) return;
      heartbeatStartedAt = Date.now();
      heartbeatPending = true;
      const revision = sessionRevisionRef.current;
      try {
        const liveToken = await getCurrentSupabaseAccessToken();
        if (!active || signingOutRef.current || revision !== sessionRevisionRef.current || !liveToken) {
          return;
        }
        safeSet(() => {
          setToken(liveToken);
          setIsAuthenticated(true);
        });
        // Skip the network round-trip while the tab is in the background; the
        // visibility/focus listeners below re-run heartbeat when it returns.
        if (typeof document !== "undefined" && document.visibilityState !== "visible") {
          return;
        }
        if (isAdminUser) {
          await adminAPI.me();
        } else {
          await authAPI.me();
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          return;
        }
      } finally {
        heartbeatPending = false;
      }
    };

    heartbeat();
    const interval = window.setInterval(heartbeat, 15000);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        heartbeat();
      }
    };

    try {
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("focus", heartbeat);
    } catch {
      // ignore
    }

    return () => {
      active = false;
      window.clearInterval(interval);
      try {
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("focus", heartbeat);
      } catch {
        // ignore
      }
    };
  }, [isAuthenticated, user?.id, user?.org_id, user?.role, user?.is_admin_user, user?.is_admin_visit, safeSet]);

  const login = useCallback(async (email, password) => {
    safeSet(() => setLoading(true));
    try {
      const res = await authAPI.login({ email, password });
      const payload = res?.data || {};
      if (!payload.access_token || !payload.refresh_token) {
        throw new Error("Anmeldung fehlgeschlagen");
      }

      const { data, error } = await supabase.auth.setSession({
        access_token: payload.access_token,
        refresh_token: payload.refresh_token,
      });
      if (error) throw error;

      if (data.session) {
        await setSessionState(data.session, { silent: true });
      } else {
        clearLocalAuth();
        safeSet(() => {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        });
      }

      return payload.user || null;
    } catch (err) {
      const message = localizeAuthErrorMessage(err, "Anmeldung fehlgeschlagen");
      const nextErr = new Error(message);
      nextErr.cause = err;
      throw nextErr;
    } finally {
      safeSet(() => setLoading(false));
    }
  }, [setSessionState, safeSet]);

  const completeRegistrationLogin = useCallback(async (payload) => {
    if (!payload?.access_token || !payload?.refresh_token || !payload?.user_id) throw new Error('Anmeldung unvollständig.');
    // Remove a previous admin visit before establishing the newly paid account.
    clearVisitAuthSession();
    clearApiCache();
    const { data, error } = await supabase.auth.setSession({
      access_token: payload.access_token, refresh_token: payload.refresh_token,
    });
    if (error) throw error;
    if (!data.session || data.session.user.id !== payload.user_id) {
      await supabase.auth.signOut({ scope: 'local' });
      await setSessionState(null, { silent: true });
      throw new Error('Der Zugang konnte nicht sicher zugeordnet werden.');
    }
    await setSessionState(data.session, { silent: true });
  }, [setSessionState]);

  const register = useCallback(async ({ email, password, org_name, first_name, last_name, plan, billing_period, avv_accepted }) => {
    safeSet(() => setLoading(true));
    try {
      const res = await authAPI.register({
        email,
        password,
        org_name,
        first_name,
        last_name,
        plan,
        billing_period,
        avv_accepted: Boolean(avv_accepted),
      });
      const payload = res?.data || {};

      if (payload.access_token && payload.refresh_token) {
        const { data, error } = await supabase.auth.setSession({
          access_token: payload.access_token,
          refresh_token: payload.refresh_token,
        });
        if (error) throw error;
        if (data.session) {
          await setSessionState(data.session, { silent: true });
        }
      } else {
        clearLocalAuth();
        safeSet(() => {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        });
      }

      return payload;
    } catch (err) {
      const message = localizeAuthErrorMessage(err, "Registrierung fehlgeschlagen");
      const nextErr = new Error(message);
      nextErr.cause = err;
      throw nextErr;
    } finally {
      safeSet(() => setLoading(false));
    }
  }, [setSessionState, safeSet]);

  const registerWithTrial = useCallback(async ({ email, password, org_name, first_name, last_name, billing_street, billing_postal_code, billing_city, billing_country, plan, billing_period, avv_accepted }) => {
    safeSet(() => setLoading(true));
    try {
      const res = await authAPI.registerWithTrial({
        email,
        password,
        org_name,
        first_name,
        last_name,
        billing_street,
        billing_postal_code,
        billing_city,
        billing_country,
        plan,
        billing_period,
        avv_accepted: Boolean(avv_accepted),
      });
      const payload = res?.data || {};

      if (payload.access_token && payload.refresh_token) {
        const { data, error } = await supabase.auth.setSession({
          access_token: payload.access_token,
          refresh_token: payload.refresh_token,
        });
        if (error) throw error;
        if (data.session) {
          await setSessionState(data.session, { silent: true });
        }
      } else {
        clearLocalAuth();
        safeSet(() => {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        });
      }

      return payload;
    } catch (err) {
      const message = localizeAuthErrorMessage(err, "Registrierung fehlgeschlagen");
      const nextErr = new Error(message);
      nextErr.cause = err;
      throw nextErr;
    } finally {
      safeSet(() => setLoading(false));
    }
  }, [setSessionState, safeSet]);

  const logout = useCallback(async () => {
    sessionRevisionRef.current += 1;
    signingOutRef.current = true;
    clearLocalAuth();
    safeSet(() => {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      setLoading(false);
    });

    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) console.warn("supabase signOut error", error);
    } finally {
      clearLocalAuth();
      safeSet(() => {
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        setLoading(false);
      });
      signingOutRef.current = false;
    }
  }, [safeSet]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated,
      login,
      completeRegistrationLogin,
      register,
      registerWithTrial,
      logout,
      refresh: hydrateFromSession,
    }),
    [user, token, loading, isAuthenticated, login, completeRegistrationLogin, register, registerWithTrial, logout, hydrateFromSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
